import { useMemo, useState } from "react";
import LargeTooltip from "../tooltips/large-tooltip";

interface TruncatedDescriptionCellProps {
  description?: string | null;
  maxChars?: number;
  className?: string;
  // Table/list cells need the preview + "View more" pinned to one line so they don't
  // wrap onto a second row within a fixed-height row. A narrow container (e.g. a
  // dashboard card widget) has no such row-height constraint and would rather wrap
  // than risk the run overflowing into a sibling element and becoming unclickable --
  // pass false there. Defaults to true so every existing table/list call site is
  // unaffected.
  noWrap?: boolean;
}

// Shared "truncate + View more" pattern for a long description column (payment
// descriptions, etc.) -- reuses LargeTooltip, the app's existing click-anchored,
// portal-rendered description popover (already used correctly by
// ServiceRequestCardItem.tsx). Extracted once a third call site needed the identical
// preview-slice + View-more-link + popover logic (see bug/ui-bugfix-batch2's plan).
const TruncatedDescriptionCell = ({
  description,
  maxChars = 30,
  className = "",
  noWrap = true,
}: TruncatedDescriptionCellProps) => {
  const [tooltipState, setTooltipState] = useState<{
    isOpen: boolean;
    position: { top: number; left: number };
  }>({
    isOpen: false,
    position: { top: 0, left: 0 },
  });

  // LargeTooltip's useLayoutEffect lists `description` in its dependency array and
  // calls setAdjustedStyle with a fresh object -- an inline <>{...}</> would be a new
  // node identity every render and re-trigger that effect on every parent re-render
  // while the tooltip is open. Memoized per FeedbackCardItem.tsx's documented rule.
  const descriptionNode = useMemo(() => <>{description}</>, [description]);

  if (!description) return <>-</>;

  const isTruncated = description.length > maxChars;
  const preview = isTruncated ? `${description.slice(0, maxChars)}...` : description;

  const handleViewMoreClick = (event: React.MouseEvent<HTMLElement>) => {
    event.preventDefault();
    event.stopPropagation();
    const rect = event.currentTarget.getBoundingClientRect();
    setTooltipState({
      isOpen: true,
      position: { top: rect.bottom + window.scrollY, left: rect.left + window.scrollX },
    });
  };

  const handleCloseTooltip = () => {
    setTooltipState((prev) => ({ ...prev, isOpen: false }));
  };

  return (
    <span className={className} style={noWrap ? { whiteSpace: "nowrap" } : undefined}>
      {preview}
      {isTruncated && (
        <button
          type="button"
          className="listing-viewmore mx-2 ml-1"
          style={{ background: "none", border: "none", padding: 0, fontFamily: "inherit", fontSize: "inherit", cursor: "pointer" }}
          onClick={handleViewMoreClick}
        >
          View more
        </button>
      )}
      <LargeTooltip
        isOpen={tooltipState.isOpen}
        onClose={handleCloseTooltip}
        // Wrapped as a ReactNode (not a plain string) so LargeTooltip's own
        // isHtmlString check is false and it renders the text as plain children
        // instead of via dangerouslySetInnerHTML -- this description is free-text
        // user input (a payment description), not markup.
        description={descriptionNode}
        position={tooltipState.position}
        title="Description"
      />
    </span>
  );
};

export default TruncatedDescriptionCell;
